import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can, type Me } from '../auth/me';
import { useMe } from '../auth/useMe';
import { ConflictNotice } from '../ui/ConflictNotice';
import { ContractsNav } from './ContractsPage';
import { CONTRACTS_KEY, CONTRACTS_SETTINGS_RESOURCE, KINDS, PLACEHOLDERS, TEMPLATES_RESOURCE, type CompanyData, type Template } from './types';
import './contracts.css';
import { sameValues, useUnsavedGuard } from '../ui/unsaved';

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (fieldIssues(e)[0]?.message ?? e.message) : fallback);

/** One template: name and text with {placeholders}; saved with its version (409 when someone saved first). */
function TemplateEditor({ template, canEdit }: { template: Template; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(template.name);
  const [body, setBody] = useState(template.body);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  // owner 2026-10-23 21:14: leaving with unsaved changes asks first
  const saveNow = useRef<() => Promise<boolean>>(async () => false);
  const templateRef = useUnsavedGuard({ dirty: canEdit && (name !== template.name || body !== template.body), save: () => saveNow.current() });
  useEffect(() => {
    setName(template.name);
    setBody(template.body);
  }, [template.version, template.name, template.body]);
  /** Saves; false when it did not (the reason is shown). */
  async function persist(): Promise<boolean> {
    setError(null);
    setSaved(false);
    const changed = { ...(name !== template.name ? { name } : {}), ...(body !== template.body ? { body } : {}) };
    if (Object.keys(changed).length === 0) return true;
    try {
      await api(`/api/contracts/templates/${template.id}`, { method: 'PATCH', body: { ...changed, version: template.version } });
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: [...CONTRACTS_KEY, 'templates'] });
      return true;
    } catch (err) {
      setError(err);
      return false;
    }
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    void persist();
  }
  saveNow.current = persist;
  return (
    <form ref={templateRef} className="ctr-form ctr-template" onSubmit={submit} aria-label={`Szablon: ${template.name}`}>
      <h2>{KINDS[template.kind]}</h2>
      {error instanceof ApiError && error.status === 409 ? (
        <ConflictNotice
          onReload={() => {
            setError(null);
            void queryClient.invalidateQueries({ queryKey: [...CONTRACTS_KEY, 'templates'] });
          }}
        />
      ) : (
        error !== null && (
          <p className="alert" role="alert">
            {errorText(error, 'Nie udało się zapisać szablonu')}
          </p>
        )
      )}
      <label className="field">
        <span>Nazwa (tytuł umowy)</span>
        <input value={name} readOnly={!canEdit} onChange={(e) => setName(e.target.value)} maxLength={200} />
      </label>
      <label className="field">
        <span>Treść</span>
        <textarea value={body} readOnly={!canEdit} onChange={(e) => setBody(e.target.value)} rows={24} />
      </label>
      {canEdit && (
        <div className="actions">
          <button className="btn primary" type="submit">
            Zapisz szablon
          </button>
          {saved && (
            <span className="muted" role="status">
              Zapisano
            </span>
          )}
        </div>
      )}
    </form>
  );
}

/** "Dane firmy do umów": name, address and NIP put into {firma}, {firma_adres}, {firma_nip}. */
function CompanyForm({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: [...CONTRACTS_KEY, 'settings'], queryFn: () => api<CompanyData>('/api/contracts/settings') });
  const [values, setValues] = useState({ companyName: '', companyAddress: '', companyNip: '' });
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const data = query.data;
  useEffect(() => {
    if (data) setValues({ companyName: data.companyName, companyAddress: data.companyAddress, companyNip: data.companyNip });
  }, [data]);
  const saveNow = useRef<() => Promise<boolean>>(async () => false);
  const companyRef = useUnsavedGuard({
    dirty: canEdit && !!data && !sameValues(values, { companyName: data.companyName, companyAddress: data.companyAddress, companyNip: data.companyNip }),
    save: () => saveNow.current(),
  });
  if (!data) return null;
  /** Saves; false when it did not (the reason is shown). */
  async function persist(): Promise<boolean> {
    setError(null);
    setSaved(false);
    const changed = Object.fromEntries(Object.entries(values).filter(([k, v]) => data![k as keyof typeof values] !== v));
    if (Object.keys(changed).length === 0) return true;
    try {
      await api('/api/contracts/settings', { method: 'PUT', body: { ...changed, version: data!.version } });
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: CONTRACTS_KEY });
      return true;
    } catch (err) {
      setError(err);
      return false;
    }
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    void persist();
  }
  saveNow.current = persist;
  const field = (key: keyof typeof values, label: string) => (
    <label className={key === 'companyAddress' ? 'field ctr-wide' : 'field'}>
      <span>{label}</span>
      <input value={values[key]} readOnly={!canEdit} onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))} />
    </label>
  );
  return (
    <form ref={companyRef} className="ctr-form" onSubmit={submit} aria-label="Dane firmy do umów">
      <h2>Dane firmy do umów</h2>
      {error instanceof ApiError && error.status === 409 ? (
        <ConflictNotice
          onReload={() => {
            setError(null);
            void query.refetch();
          }}
        />
      ) : (
        error !== null && (
          <p className="alert" role="alert">
            {errorText(error, 'Nie udało się zapisać')}
          </p>
        )
      )}
      <div className="ctr-grid">
        {field('companyName', 'Nazwa firmy')}
        {field('companyNip', 'NIP')}
        {field('companyAddress', 'Adres')}
      </div>
      {canEdit && (
        <div className="actions">
          <button className="btn" type="submit">
            Zapisz dane firmy
          </button>
          {saved && (
            <span className="muted" role="status">
              Zapisano
            </span>
          )}
        </div>
      )}
    </form>
  );
}

function Templates({ me }: { me: Me }) {
  const list = useQuery({ queryKey: [...CONTRACTS_KEY, 'templates'], queryFn: () => api<{ templates: Template[] }>('/api/contracts/templates') });
  const canEdit = can(me, TEMPLATES_RESOURCE, 'edit');
  return (
    <section aria-label="Szablony umów" data-feature-id="contracts.templates">
      <p className="lede">Treść umów z polami w nawiasach klamrowych. Szablony startowe są proste — podmień je na własne wzory.</p>
      <p className="ctr-placeholders muted">
        Pola:{' '}
        {Object.entries(PLACEHOLDERS).map(([k, label]) => (
          <code key={k} title={label}>{`{${k}}`}</code>
        ))}
      </p>
      {can(me, CONTRACTS_SETTINGS_RESOURCE, 'view') && <CompanyForm canEdit={can(me, CONTRACTS_SETTINGS_RESOURCE, 'edit')} />}
      {list.error && <p className="alert" role="alert">{errorText(list.error, 'Nie udało się wczytać szablonów')}</p>}
      {(list.data?.templates ?? []).map((t) => (
        <TemplateEditor key={t.id} template={t} canEdit={canEdit} />
      ))}
    </section>
  );
}

/** `/finanse/umowy/szablony`. */
export function TemplatesPage() {
  const { me } = useMe();
  if (!me) return null;
  return (
    <section className="page wide finance-page">
      <ContractsNav me={me} />
      <Templates me={me} />
    </section>
  );
}
